import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { Notice } from "@/components/domains/parts";
import { Mailboxes } from "@/components/email/mailboxes";
import { Badge, Button, ErrorAlert, Section, Select, type Requirement } from "@/components/ui";
import { buttonClass } from "@/components/ui/Button";
import {
  KIND_LABEL,
  KIND_PURPOSE,
  STATE_PILL,
  connectProblem,
  emailApi,
  emailKeys,
  type EligibleDomain,
  type EmailCheck,
  type EmailDomain,
  type EmailEntitlement,
} from "@/lib/email";
import { formatDate, formatDateTime, timeAgo } from "@/lib/format";

/** A line read aloud to screen reader users when a connect or a check finishes (a change they cannot otherwise see). */
export function Announcer({ message }: { message: string }) {
  return (
    <div role="status" aria-live="polite" className="sr-only">
      {message}
    </div>
  );
}

/**
 * One connected domain: the four records, when DNS was read, Retry verification, and what stops mailboxes. Used by the
 * domain page's Email tab and by the Email page, so both say the same thing the same way.
 */
export function EmailDomainStatus({
  row,
  onAnnounce,
}: {
  row: EmailDomain;
  onAnnounce: (message: string) => void;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const manage: Requirement = { permission: "email:manage", on: ["domain_folder", row.folder_id] };
  const refresh = useMutation({
    mutationFn: () => emailApi.refresh(orgId, row.id),
    onSuccess: async (next) => {
      await qc.invalidateQueries({ queryKey: emailKeys.all(orgId) });
      onAnnounce(
        next.recently_checked
          ? `${next.fqdn}: checked a moment ago. Showing that result.`
          : next.lookup_failed
            ? `${next.fqdn}: we could not reach DNS for every record. Showing the last result.`
            : `${next.fqdn}: DNS checked, ${STATE_PILL[next.state].label}.`,
      );
    },
  });
  return (
    <div className="flex flex-col gap-5">
      <DnsStatus
        row={row}
        manage={manage}
        checking={refresh.isPending}
        onRetry={() => refresh.mutate()}
        error={refresh.error}
        reference={refresh.data?.operation_reference ?? null}
        recentlyChecked={refresh.data?.recently_checked ?? false}
      />
      <ProvisioningNotice row={row} />
      <Mailboxes row={row} onAnnounce={onAnnounce} />
    </div>
  );
}

/**
 * Connect a domain to e-mail. ``choices`` are the domains the person may connect (the server already filtered them);
 * ``requires`` is the permission to show on the button when the caller knows the domain's folder.
 */
export function ConnectDomain({
  choices,
  providers,
  entitlement,
  requires,
  onAnnounce,
  subtitle,
}: {
  choices: EligibleDomain[];
  providers: string[];
  entitlement: EmailEntitlement;
  requires?: Requirement;
  onAnnounce: (message: string) => void;
  subtitle: string;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [picked, setPicked] = useState("");
  const chosen = choices.find((c) => c.domain_id === picked) ?? choices[0];
  const connect = useMutation({
    mutationFn: (input: { domainId: string; provider: string }) =>
      emailApi.connect(orgId, input.domainId, input.provider),
    onSuccess: async (row) => {
      await qc.invalidateQueries({ queryKey: emailKeys.all(orgId) });
      onAnnounce(`Email is connected for ${row.fqdn}. Check its DNS records next.`);
    },
  });
  return (
    <Section title="Professional email" subtitle={subtitle}>
      {providers.length === 0 ? (
        <Notice tone="cream" title="Professional email isn't available yet">
          Nothing has been changed. We'll tell you here when you can connect it.
        </Notice>
      ) : choices.length === 0 ? (
        <p className="text-sm text-fg-muted">
          There is no domain left to connect. A domain must be registered with CortexGrip, and you need the
          Email Manager role for its folder.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="max-w-[640px] text-sm text-fg-muted">
            Connect {choices.length === 1 ? choices[0]!.fqdn : "a domain"} to professional email. We'll then
            show the DNS records it needs and check them for you.
          </p>
          {choices.length > 1 && (
            <Select
              aria-label="Domain to connect"
              value={chosen?.domain_id ?? ""}
              onChange={(e) => setPicked(e.target.value)}
              className="max-w-[320px]"
            >
              {choices.map((c) => (
                <option key={c.domain_id} value={c.domain_id}>
                  {c.fqdn}
                </option>
              ))}
            </Select>
          )}
          <div>
            <Button
              requires={requires}
              disabledReason={
                entitlement.enabled
                  ? undefined
                  : "Your plan doesn't include professional mailboxes. Upgrade first."
              }
              loading={connect.isPending}
              onClick={() =>
                chosen && connect.mutate({ domainId: chosen.domain_id, provider: providers[0]! })
              }
            >
              {connect.isPending ? "Connecting…" : "Connect email"}
            </Button>
          </div>
          <ConnectError error={connect.error} />
        </div>
      )}
    </Section>
  );
}

function ConnectError({ error }: { error: unknown }) {
  const problem = connectProblem(error);
  if (!problem) return null;
  return (
    <div className="flex flex-col gap-1">
      <ErrorAlert error={error} />
      {problem.nextAction && <p className="text-sm text-fg-muted">{problem.nextAction}</p>}
    </div>
  );
}

export function EntitlementNotice({ entitlement }: { entitlement: EmailEntitlement }) {
  if (!entitlement.enabled) {
    return (
      <Notice
        tone="warning"
        title="Professional email isn't in your plan"
        actions={
          <Link to="/billing/plans" className={buttonClass("secondary")}>
            View plans
          </Link>
        }
      >
        {entitlement.reason ?? "Your current plan does not include professional mailboxes."} What is already
        set up stays visible.
      </Notice>
    );
  }
  if (entitlement.ending) {
    return (
      <Notice
        tone="warning"
        title={
          entitlement.ends_on
            ? `Professional email ends ${formatDate(entitlement.ends_on)}`
            : "Professional email is ending"
        }
        actions={
          <Link to="/billing/plans" className={buttonClass("secondary")}>
            Change plan
          </Link>
        }
      >
        {entitlement.reason}
      </Notice>
    );
  }
  return null;
}

function DnsStatus({
  row,
  manage,
  checking,
  onRetry,
  error,
  reference,
  recentlyChecked,
}: {
  row: EmailDomain;
  manage: Requirement;
  checking: boolean;
  onRetry: () => void;
  error: unknown;
  reference: string | null;
  recentlyChecked: boolean;
}) {
  const pill = STATE_PILL[row.state];
  return (
    <Section
      title="Email DNS status"
      subtitle={`MX, SPF, DKIM and DMARC records for ${row.fqdn}`}
      action={
        <Button variant="secondary" requires={manage} loading={checking} onClick={onRetry}>
          {checking ? "Checking…" : "Retry verification"}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
          <Badge tone={pill.tone} size="md">
            {pill.label}
          </Badge>
          <LastChecked at={row.last_checked_at} />
        </div>

        {row.stale && (
          <Notice tone="warning" title="This result may be out of date">
            The records were last read {timeAgo(row.last_checked_at)}. Retry verification to read them again.
          </Notice>
        )}
        {row.lookup_failed && (
          <Notice tone="warning" title="We couldn't reach DNS for every record">
            Showing the last result we have. Nothing was changed. Try again in a few minutes.
          </Notice>
        )}
        {recentlyChecked && (
          <p className="text-sm text-fg-muted">Checked a moment ago. Try again in about 30 seconds.</p>
        )}
        {reference && (
          <p className="text-sm text-fg-muted">
            Reference <span className="font-mono">{reference}</span>. Quote it if you contact support.
          </p>
        )}
        <ErrorAlert error={error} />

        {row.checks.length === 0 ? (
          <p className="text-sm text-fg-muted">
            DNS has not been checked yet. Choose Retry verification to read the records.
          </p>
        ) : (
          <ChecksTable fqdn={row.fqdn} checks={row.checks} />
        )}
      </div>
    </Section>
  );
}

function LastChecked({ at }: { at: string | null }) {
  if (!at) return <span>Not checked yet</span>;
  return (
    <span>
      Last checked <time dateTime={at}>{timeAgo(at)}</time> ({formatDateTime(at)})
    </span>
  );
}

function ChecksTable({ fqdn, checks }: { fqdn: string; checks: EmailCheck[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-left text-sm">
        <caption className="sr-only">DNS records professional email needs on {fqdn}</caption>
        <thead>
          <tr className="border-b border-border text-xs text-fg-subtle">
            <th scope="col" className="py-2 pr-3 font-semibold">
              Record
            </th>
            <th scope="col" className="py-2 pr-3 font-semibold">
              Status
            </th>
            <th scope="col" className="py-2 pr-3 font-semibold">
              What we expect
            </th>
            <th scope="col" className="py-2 pr-3 font-semibold">
              What we found
            </th>
            <th scope="col" className="py-2 font-semibold">
              What to do
            </th>
          </tr>
        </thead>
        <tbody>
          {checks.map((c) => {
            const pill = STATE_PILL[c.state];
            return (
              <tr key={c.kind} className="border-b border-border align-top last:border-0">
                <th scope="row" className="py-3 pr-3 font-semibold text-fg">
                  {KIND_LABEL[c.kind]}
                  <span className="block text-xs font-normal text-fg-subtle">{KIND_PURPOSE[c.kind]}</span>
                </th>
                <td className="py-3 pr-3">
                  <Badge tone={pill.tone}>{pill.label}</Badge>
                </td>
                <td className="py-3 pr-3">
                  <Values hostname={c.hostname} type={c.record_type} values={c.expected} />
                </td>
                <td className="py-3 pr-3">
                  <Values
                    hostname={c.hostname}
                    type={c.record_type}
                    values={c.observed}
                    empty="Nothing found"
                    note={c.reachable ? undefined : "Last seen"}
                  />
                </td>
                <td className="py-3 text-fg-muted">{c.corrective_action}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Values({
  hostname,
  type,
  values,
  empty,
  note,
}: {
  hostname: string;
  type: string;
  values: string[];
  empty?: string;
  note?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-fg-subtle">
        {type} on {hostname}
        {note ? ` · ${note}` : ""}
      </span>
      {values.length === 0 ? (
        <span className="text-fg-muted">{empty ?? "—"}</span>
      ) : (
        values.map((v) => (
          <code key={v} className="rounded bg-muted px-1.5 py-0.5 text-xs break-all">
            {v}
          </code>
        ))
      )}
    </div>
  );
}

/** Why no mailbox can be added on this domain now (nothing when one can: the Mailboxes section is the next step). */
function ProvisioningNotice({ row }: { row: EmailDomain }) {
  const p = row.provisioning;
  if (p.allowed) return null;
  return (
    <Notice
      tone={p.reason === "EMAIL_NOT_ENTITLED" ? "warning" : "cream"}
      title="Mailboxes can't be added yet"
    >
      {p.message} {p.next_action}
    </Notice>
  );
}
